import { useEffect, useId, useMemo, useRef, useState } from "react"
import { useRouter } from "next/router"

import { Button, Textarea } from "@coval/ui"

import { parseEmailRecipientAllowlist } from "@/utils/emailRecipientAllowlist"
import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

import { useEmailRecipientAllowlistTransport } from "../../routes/useEmailRecipientAllowlistTransport"

function readApprovals(settings: Record<string, unknown> | undefined) {
  const availableSettings = settings ?? {}
  const recipients =
    "approved_email_recipients" in availableSettings
      ? availableSettings.approved_email_recipients
      : []
  const domains =
    "approved_email_domains" in availableSettings
      ? availableSettings.approved_email_domains
      : []
  if (
    !Array.isArray(recipients) ||
    !recipients.every((value) => typeof value === "string") ||
    !Array.isArray(domains) ||
    !domains.every((value) => typeof value === "string")
  ) {
    return null
  }
  return {
    recipients: recipients.join("\n"),
    domains: domains.join("\n"),
    recipientValues: recipients,
    domainValues: domains,
  }
}

function EmailRecipientAllowlistFields() {
  const recipientsId = useId()
  const domainsId = useId()
  const { settingsQuery, updateSettings, invalidateSettings, isConflictError } =
    useEmailRecipientAllowlistTransport()
  const settings = settingsQuery.data?.settings
  const hasResponse = settingsQuery.data !== undefined
  const approvals = useMemo(
    () => (hasResponse ? readApprovals(settings) : null),
    [hasResponse, settings],
  )
  const active = useRef(true)
  const appliedApprovals = useRef<typeof approvals>(null)
  const [draftRecipients, setDraftRecipients] = useState("")
  const [draftDomains, setDraftDomains] = useState("")
  const [savedRecipients, setSavedRecipients] = useState("")
  const [savedDomains, setSavedDomains] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [saving, setSaving] = useState(false)
  const recipientsChanged = draftRecipients !== savedRecipients
  const domainsChanged = draftDomains !== savedDomains
  const dirty = recipientsChanged || domainsChanged

  const loadCurrentApprovals = (current: NonNullable<typeof approvals>) => {
    setDraftRecipients(current.recipients)
    setDraftDomains(current.domains)
    setSavedRecipients(current.recipients)
    setSavedDomains(current.domains)
    setSaved(false)
  }

  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])

  useEffect(() => {
    if (approvals && !dirty && appliedApprovals.current !== approvals) {
      appliedApprovals.current = approvals
      setDraftRecipients(approvals.recipients)
      setDraftDomains(approvals.domains)
      setSavedRecipients(approvals.recipients)
      setSavedDomains(approvals.domains)
      if (
        approvals.recipients !== savedRecipients ||
        approvals.domains !== savedDomains
      ) {
        setSaved(false)
      }
    }
  }, [approvals, dirty, savedRecipients, savedDomains])

  const save = async () => {
    if (!approvals || !dirty || saving || updateSettings.isPending) {
      return
    }
    setSaving(true)
    try {
      const refreshed = await settingsQuery.refetch()
      if (!active.current) {
        return
      }
      const current = readApprovals(refreshed.data?.settings)
      if (refreshed.isError || refreshed.data === undefined) {
        throw new Error("Could not refresh organization approvals. Try again.")
      }
      if (!current) {
        throw new Error("Organization email approvals are unavailable.")
      }
      const parsed = parseEmailRecipientAllowlist(
        recipientsChanged ? draftRecipients : "",
        domainsChanged ? draftDomains : "",
      )
      if (
        parsed.approvedEmailRecipients.length > 500 ||
        parsed.approvedEmailDomains.length > 500
      ) {
        throw new Error("Each approval list can contain up to 500 entries.")
      }
      const submittedRecipients = parsed.approvedEmailRecipients.join("\n")
      const submittedDomains = parsed.approvedEmailDomains.join("\n")
      const alreadySaved =
        (!recipientsChanged || current.recipients === submittedRecipients) &&
        (!domainsChanged || current.domains === submittedDomains)
      if (!alreadySaved) {
        if (
          (recipientsChanged && current.recipients !== savedRecipients) ||
          (domainsChanged && current.domains !== savedDomains)
        ) {
          loadCurrentApprovals(current)
          throw new Error(
            "Organization approvals changed while you were editing. Latest values loaded; edit and save again.",
          )
        }
        await updateSettings.mutateAsync({
          ...(recipientsChanged && {
            approved_email_recipients: parsed.approvedEmailRecipients,
            expected_approved_email_recipients: current.recipientValues,
          }),
          ...(domainsChanged && {
            approved_email_domains: parsed.approvedEmailDomains,
            expected_approved_email_domains: current.domainValues,
          }),
        })
      }
      if (!active.current) {
        return
      }
      const nextRecipients = recipientsChanged
        ? submittedRecipients
        : current.recipients
      const nextDomains = domainsChanged ? submittedDomains : current.domains
      setDraftRecipients(nextRecipients)
      setDraftDomains(nextDomains)
      setSavedRecipients(nextRecipients)
      setSavedDomains(nextDomains)
      setError(null)
      setSaved(true)
    } catch (failure) {
      if (!active.current) {
        return
      }
      if (isConflictError(failure)) {
        const refreshed = await settingsQuery.refetch()
        if (!active.current) {
          return
        }
        const current = readApprovals(refreshed.data?.settings)
        if (!refreshed.isError && refreshed.data !== undefined && current) {
          loadCurrentApprovals(current)
          setError(
            "Organization approvals changed while you were editing. Latest values loaded; edit and save again.",
          )
          return
        }
      }
      setError(
        failure instanceof Error
          ? failure.message
          : "Could not save email approvals. Try again.",
      )
      setSaved(false)
    } finally {
      if (active.current) {
        setSaving(false)
      }
      void invalidateSettings()
    }
  }

  return (
    <div className="space-y-3 border-t border-coval-border pt-4">
      <div>
        <h3 className="text-sm font-semibold text-coval-text">
          Organization email approvals
        </h3>
        <p className="text-sm text-coval-text-muted">
          Applies to every Email agent in this organization. Approve the
          recipient address or its exact domain before running a simulation.
        </p>
      </div>
      {settingsQuery.isLoading && !settingsQuery.data && (
        <p className="text-sm text-coval-text-muted">Loading approvals…</p>
      )}
      {settingsQuery.isError && (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-coval-danger-on-surface">
            Could not load organization email approvals.
          </p>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void settingsQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      )}
      {settingsQuery.data && !approvals && (
        <p role="alert" className="text-sm text-coval-danger-on-surface">
          Organization email approvals are unavailable. Contact support before
          changing them.
        </p>
      )}
      {approvals && (
        <>
          <div className="space-y-1">
            <label
              htmlFor={recipientsId}
              className="text-sm font-medium text-coval-text"
            >
              Approved email addresses
            </label>
            <Textarea
              id={recipientsId}
              aria-label="Approved email addresses"
              value={draftRecipients}
              onChange={(event) => {
                setDraftRecipients(event.target.value)
                setSaved(false)
                setError(null)
              }}
              placeholder="agent@example.com"
              disabled={saving}
            />
            <p className="text-xs text-coval-text-muted">
              One exact address per line.
            </p>
          </div>
          <div className="space-y-1">
            <label
              htmlFor={domainsId}
              className="text-sm font-medium text-coval-text"
            >
              Approved domains
            </label>
            <Textarea
              id={domainsId}
              aria-label="Approved domains"
              value={draftDomains}
              onChange={(event) => {
                setDraftDomains(event.target.value)
                setSaved(false)
                setError(null)
              }}
              placeholder="example.com"
              disabled={saving}
            />
            <p className="text-xs text-coval-text-muted">
              One exact domain per line. Subdomains need their own entry.
            </p>
          </div>
          {error && (
            <p role="alert" className="text-sm text-coval-danger-on-surface">
              {error}
            </p>
          )}
          {saved && (
            <p role="status" className="text-sm text-coval-text-muted">
              Organization approvals saved.
            </p>
          )}
          <Button
            type="button"
            variant="secondary"
            disabled={!dirty || saved || saving}
            onClick={() => void save()}
          >
            {saving ? "Saving approvals…" : "Save approvals"}
          </Button>
        </>
      )}
    </div>
  )
}

export function EmailRecipientAllowlistConfig() {
  const router = useRouter()
  const organizationSlug = getRouteOrganizationSlug(router.asPath)
  return <EmailRecipientAllowlistFields key={organizationSlug} />
}
